import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { SETTINGS_BACKLOG_PATH } from '@/constants/settings-tabs'
import type { ProjectOption } from '@ai-meeting/shared'
import Combobox from '@/components/Combobox'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchProjects } from '@/services/projects'

type LoadState = 'loading' | 'ready' | 'empty' | 'error' | 'connect' | 'reauth'

interface ProjectSelectProps {
  id: string
  /** Backlog's project key, or `''` for no project. */
  value: string
  onChange: (projectKey: string) => void
  disabled?: boolean
  /** Adds the Create Meeting-only fallback for work outside a Backlog project. */
  includeOther?: boolean
  /** Inline beside a heading: see {@link ComboboxProps.compact}. */
  compact?: boolean
  /**
   * Run as the reader follows the settings link out of here.
   *
   * The create form passes its own close, because the settings page would
   * otherwise render behind a modal nobody dismissed. Screens that are not a
   * modal pass nothing.
   */
  onOpenSettings?: () => void
}

/** UI-only value for the Create Meeting “Other” row; never send it to an API. */
export const OTHER_PROJECT_OPTION_VALUE = '$other'

/**
 * Picks the project a meeting belongs to.
 *
 * The list is whatever the reader's own Backlog API key can see, so it is
 * shorter and more relevant than the space-wide register it replaced — and it
 * is empty until they connect. That is a state with something to do about it
 * rather than a failure, so it reads as an invitation to the settings dialog
 * instead of an error.
 *
 * A searchable panel rather than a native `select`: scrolling for the project
 * whose code you already know is slower than typing three letters of it.
 *
 * Failing to load is never fatal. The project is optional on the meeting, so
 * an unreachable Backlog leaves the field disabled with a reason underneath
 * and the rest of the form submits exactly as before.
 */
export default function ProjectSelect({
  id,
  value,
  onChange,
  disabled = false,
  includeOther = false,
  compact = false,
  onOpenSettings,
}: ProjectSelectProps) {
  const { t } = useUiStrings()
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Loads on mount rather than on first open: the form is short-lived, so the
  // choice should already be there when the eye reaches this field. Connecting
  // Backlog leaves this form, so coming back is a fresh mount and a fresh ask.
  useEffect(() => {
    let active = true

    void fetchProjects()
      .then((response) => {
        if (!active) return
        setProjects(response.projects)
        if (response.status === 'CONNECTION_REQUIRED') return setLoadState('connect')
        if (response.status === 'REAUTH_REQUIRED') return setLoadState('reauth')
        setLoadState(response.projects.length > 0 || includeOther ? 'ready' : 'empty')
      })
      .catch((error: unknown) => {
        if (!active) return
        setErrorMessage(error instanceof Error ? error.message : null)
        setLoadState('error')
      })

    return () => {
      active = false
    }
  }, [includeOther])

  const options = useMemo(
    () => [
      ...projects.map((project) => ({
        value: project.projectKey,
        // Code first: it is what people quote to each other, and several names
        // in the space differ only by a trailing word.
        label: `${project.projectKey} — ${project.name}`,
      })),
      ...(includeOther
        ? [{ value: OTHER_PROJECT_OPTION_VALUE, label: t.projectOther }]
        : []),
    ],
    [includeOther, projects, t.projectOther]
  )

  /** The two states the reader can act on, and the only two offering the link. */
  const needsKey = loadState === 'connect' || loadState === 'reauth'

  return (
    <div className="space-y-1">
      <Combobox
        id={id}
        label={t.projectLabel}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={PLACEHOLDERS[loadState](t)}
        searchLabel={t.projectSearch}
        emptyLabel={t.projectSearchEmpty}
        disabled={disabled || loadState !== 'ready'}
        compact={compact}
      />

      {/* Below the control, not inside the panel: a panel only opens when it is
          clicked, and a failure nobody can see until they click is a failure
          they will read as an empty list. */}
      {loadState === 'error' && (
        <p role="alert" className="text-xs text-red-500">
          {errorMessage ?? t.failedLoadProjects}
        </p>
      )}
      {loadState === 'empty' && (
        <p className="text-xs text-[var(--text-secondary)]">{t.projectNoneHint}</p>
      )}
      {needsKey && (
        <p className="text-xs text-[var(--text-secondary)]">
          {t.projectConnectHint}{' '}
          <Link
            to={SETTINGS_BACKLOG_PATH}
            onClick={onOpenSettings}
            className="font-semibold text-[var(--accent)] underline underline-offset-2"
          >
            {t.projectOpenSettings}
          </Link>
        </p>
      )}
    </div>
  )
}

/** What the closed control says in each state, since it is the only line most readers see. */
const PLACEHOLDERS: Record<LoadState, (t: ReturnType<typeof useUiStrings>['t']) => string> = {
  loading: (t) => t.projectLoading,
  ready: (t) => t.projectPlaceholder,
  empty: (t) => t.projectNone,
  error: (t) => t.projectLoadFailed,
  connect: (t) => t.projectConnectRequired,
  reauth: (t) => t.projectReauthRequired,
}
